import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import { 
  Search, 
  Grid, 
  ChevronLeft, 
  ChevronRight,
  Plus,
  Download,
  MoreVertical,
  X,
  Clock,
  Briefcase,
  User,
  LayoutGrid,
  Menu,
  ArrowUpDown
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/Submissions.css';
import { usePersistentFilters } from '../../hooks/usePersistentFilters';

// hiringSteps are now fetched from the API to ensure consistency across the application.


const ProgressChevron = ({ 
  activeStep = 0, 
  onStepClick, 
  counts = [], 
  rejectedStepCounts = [],
  withdrawnStepCounts = [],
  hiredStepCounts = [],
  hiringSteps = []
}) => {
  const stepsCount = hiringSteps.length > 0 ? hiringSteps.length : 10;
  return (
    <div className="progress-chevron-wrapper">
      <div className={`progress-chevron-bar total-${stepsCount}-steps`}>
        {[...Array(stepsCount)].map((_, index) => {
          const activeCount = counts[index] || 0;
          const rejCount = rejectedStepCounts[index] || 0;
          const withCount = withdrawnStepCounts[index] || 0;
          const hCount = hiredStepCounts[index] || 0;
          const totalAtStep = activeCount + rejCount + withCount + hCount;
          
          const stageName = hiringSteps[index]?.stepName || `Phase ${index + 1}`;
          
          let stepClass = 'empty';
          let tooltipDesc = `${stageName}: 0 Candidates`;

          if (rejCount > 0 && activeCount === 0 && withCount === 0 && hCount === 0) {
            stepClass = 'is-rejected';
            tooltipDesc = `${stageName}: ${rejCount} Rejected Candidate${rejCount === 1 ? '' : 's'}`;
          } else if (withCount > 0 && activeCount === 0 && rejCount === 0 && hCount === 0) {
            stepClass = 'is-withdrawn';
            tooltipDesc = `${stageName}: ${withCount} Withdrawn Candidate${withCount === 1 ? '' : 's'}`;
          } else if (hCount > 0 && activeCount === 0 && rejCount === 0 && withCount === 0) {
            stepClass = 'is-hired';
            tooltipDesc = `${stageName}: ${hCount} Hired Candidate${hCount === 1 ? '' : 's'}`;
          } else if (activeCount > 0 && rejCount === 0 && withCount === 0 && hCount === 0) {
            stepClass = 'is-active';
            tooltipDesc = `${stageName}: ${activeCount} Active Candidate${activeCount === 1 ? '' : 's'}`;
          } else if (totalAtStep > 0) {
            stepClass = 'is-mixed';
            const parts = [];
            if (activeCount > 0) parts.push(`${activeCount} Active`);
            if (rejCount > 0) parts.push(`${rejCount} Rejected`);
            if (withCount > 0) parts.push(`${withCount} Withdrawn`);
            if (hCount > 0) parts.push(`${hCount} Hired`);
            tooltipDesc = `${stageName}: ${parts.join(', ')}`;
          }
          
          return (
            <div 
              key={index} 
              className={`chevron-step step-${index + 1} ${stepClass}`}
              onClick={(e) => {
                e.stopPropagation();
                onStepClick && onStepClick(index + 1);
              }}
              title={tooltipDesc}
            >
              {totalAtStep}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const isUSLocation = (loc) => {
  if (!loc) return false;
  const normalized = loc.toLowerCase().trim();
  return normalized.endsWith(', us') || 
         normalized.endsWith(' us') || 
         normalized.endsWith(', usa') || 
         normalized.endsWith(' usa') || 
         normalized.includes('united states') ||
         normalized === 'us' ||
         normalized === 'usa';
};

const isOffshoreLocation = (loc) => {
  if (!loc) return false;
  if (isUSLocation(loc)) return false;
  const normalized = loc.toLowerCase().trim();
  return normalized.includes('india') || 
         normalized.includes('offshore') || 
         normalized.endsWith(', in') || 
         normalized.endsWith(' in') ||
         normalized === 'in' ||
         normalized.includes(', in ') ||
         normalized.includes('(in)');
};

const isItemOffshore = (loc, clientName) => {
  // Explicit physical location in US takes top precedence
  if (isUSLocation(loc)) {
    return false;
  }

  // Explicit physical location in India takes top precedence
  if (isOffshoreLocation(loc)) {
    return true;
  }

  // Fallback to client name hint only when location is absent or ambiguous
  if (clientName) {
    const cLower = clientName.toLowerCase();
    if (clientName.includes('(IN)') || cLower.includes('(india)') || cLower.includes('offshore')) {
      return true;
    }
    if (clientName.includes('(US)') || cLower.includes('(usa)')) {
      return false;
    }
  }
  return false;
};

const toYMD = (d) => {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isRoleInDateRange = (item, startStr, endStr) => {
  if (!startStr || !endStr) return true;
  const start = new Date(`${startStr}T00:00:00.000Z`);
  const end = new Date(`${endStr}T23:59:59.999Z`);

  // Check 1: Job was created within date range
  if (item.jobCreatedAt) {
    const jDate = new Date(item.jobCreatedAt);
    if (jDate >= start && jDate <= end) return true;
  }

  // Check 2: Effective/latest submission createdAt falls in date range
  if (item.createdAt) {
    const cDate = new Date(item.createdAt);
    if (cDate >= start && cDate <= end) return true;
  }

  // Check 3: Any individual submission date falls within range
  if (item.submissionDates) {
    const dates = typeof item.submissionDates === 'string' ? item.submissionDates.split(',') : [];
    for (const dStr of dates) {
      if (dStr >= startStr && dStr <= endStr) return true;
    }
  }

  return false;
};

const SubmissionDashboard = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [pageSize, setPageSize] = useState(() => {
    const saved = sessionStorage.getItem('submission_pageSize');
    return saved !== null ? parseInt(saved, 10) : 25;
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    const saved = sessionStorage.getItem('submission_searchQuery');
    return saved !== null ? saved : '';
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [siteFilter, setSiteFilter] = useState(() => {
    const saved = sessionStorage.getItem('submission_siteFilter');
    return saved !== null ? saved : 'Onsite';
  });
  const [selectedClient, setSelectedClient] = useState(() => {
    const saved = sessionStorage.getItem('submission_selectedClient');
    return saved !== null ? saved : '';
  });

  useEffect(() => {
    sessionStorage.setItem('submission_pageSize', pageSize);
  }, [pageSize]);

  useEffect(() => {
    sessionStorage.setItem('submission_searchQuery', searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    sessionStorage.setItem('submission_siteFilter', siteFilter);
  }, [siteFilter]);

  useEffect(() => {
    sessionStorage.setItem('submission_selectedClient', selectedClient);
  }, [selectedClient]);
  
  const today = toYMD(new Date());
  const initialDateFilters = {
    quickSelect: 'Today',
    startDate: today,
    endDate: today,
    submissionStatusFilter: 'with_submissions'
  };
  const { filters: dateFilters, setFilters: setDateFilters, handleClearFilter: handleClearDateFilters, handleRefresh: handlePageRefresh } = usePersistentFilters('submissionDateFilters', initialDateFilters);

  const [appliedDateFilters, setAppliedDateFilters] = useState({
    startDate: dateFilters.startDate,
    endDate: dateFilters.endDate
  });
  
  // Advanced Filter Rules
  const [filterRules, setFilterRules] = useState([
    { id: 1, field: '', condition: '', value: '', operator: 'AND' }
  ]);
  
  // State for data to allow interactivity
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = sessionStorage.getItem('submission_currentPage');
    return saved !== null ? parseInt(saved, 10) : 1;
  });

  useEffect(() => {
    sessionStorage.setItem('submission_currentPage', currentPage);
  }, [currentPage]);

  const [hiringSteps, setHiringSteps] = useState([]);
  const [searchType, setSearchType] = useState('jobs'); // 'jobs' or 'candidates'
  const [candidatesData, setCandidatesData] = useState([]);
  const submissionStatusFilter = (dateFilters.submissionStatusFilter && dateFilters.submissionStatusFilter !== 'all') ? dateFilters.submissionStatusFilter : 'with_submissions';
  const setSubmissionStatusFilter = (v) => setDateFilters(p => ({ ...p, submissionStatusFilter: typeof v === 'function' ? v(p.submissionStatusFilter || 'with_submissions') : v }));
  // Role counts and filtered list are computed below baseFilteredJobs

  useEffect(() => {
    if (searchType === 'jobs') {
      fetchSubmissions();
    } else {
      fetchAllSubmissions();
    }
    fetchHiringSteps();
  }, [searchType]);

  const fetchHiringSteps = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/recruitment/applications/hiring-steps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const progressive = (response.data || []).filter(s => {
        const code = (s.stageCode || '').toUpperCase();
        const name = (s.stepName || '').toLowerCase().trim();
        return !['REJECTED', 'WITHDRAWN', 'REJECTED_INTERNAL', 'WITHDRAWN_INTERNAL', 'HOLD', 'ON_HOLD', 'HOLD_INTERNAL'].includes(code) &&
               !name.includes('rejected') && !name.includes('withdrawn') && !name.includes('hold');
      });
      setHiringSteps(progressive);
    } catch (error) {
      console.error('Error fetching hiring steps:', error);
    }
  };

  const fetchAllSubmissions = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/recruitment/applications/all-submissions`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const mappedCandidates = response.data.map(cand => {
        let loc = cand.location || 'N/A';
        if (loc !== 'N/A') {
          loc = loc.split(',')
            .map(p => p.trim())
            .filter(p => p && p !== '.')
            .join(', ');
        }
        return {
          ...cand,
          id: cand.id,
          candidateName: cand.candidateName || 'N/A',
          jobCode: cand.jobCode ? `JOB-${String(cand.jobCode).padStart(5, '0')}` : 'N/A',
          jobTitle: cand.jobTitle || 'N/A',
          clientName: cand.clientName || 'N/A',
          email: cand.candidateEmail || 'N/A',
          phone: cand.candidatePhone || 'N/A',
          experience: cand.experience || 'N/A',
          expectedSalary: cand.expectedSalary || 'N/A',
          location: loc,
          status: cand.status || 'ACTIVE',
          progress: cand.currentStep || 0,
          submittedBy: cand.submittedBy || 'N/A',
          createdAt: cand.appliedAt,
          jobLocation: cand.jobLocation || 'N/A'
        };
      });

      setCandidatesData(mappedCandidates);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching all candidate submissions:', error);
      setIsLoading(false);
    }
  };



  const fetchSubmissions = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/recruitment/applications/submitted-roles`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Map API data to component structure if needed
      const mappedSubmissions = response.data.map(role => {
        let loc = role.location || 'N/A';
        if (loc !== 'N/A') {
          loc = loc.split(',')
            .map(p => p.trim())
            .filter(p => p && p !== '.')
            .join(', ');
        }

        return {
          id: role.id,
          jobId: `JOB-${String(role.id).padStart(5, '0')}`,
          externalJobRef: role.externalJobRef || role.jobId || 'N/A',
          clientName: role.clientName || 'N/A',
          jobTitle: role.jobTitle || 'N/A',
          employmentType: role.employmentType || 'Contract',
          experience: role.experience || 'N/A',
          location: loc,
          rate: `${role.currency || '$'} ${role.minRate || 0} - ${role.maxRate || 0}`,
          progress: role.progress === null ? 0 : role.progress,
          applicationCount: role.applicationCount !== undefined ? role.applicationCount : 0,
          stepCounts: role.stepCounts || '',
          rejectedStepCounts: role.rejectedStepCounts || '',
          withdrawnStepCounts: role.withdrawnStepCounts || '',
          hiredStepCounts: role.hiredStepCounts || '',
          rejectedCount: Number(role.rejectedCount) || 0,
          rejectedBreakdown: role.rejectedBreakdown || '',
          withdrawnCount: Number(role.withdrawnCount) || 0,
          hiredCount: Number(role.hiredCount) || 0,
          createdAt: role.createdAt,
          jobCreatedAt: role.jobCreatedAt || role.createdAt,
          lastSubmissionAt: role.lastSubmissionAt || null,
          submissionDates: role.submissionDates || '',
          recruiters: role.recruiters || ['A'],
          description: role.jobDescription || '',
          recruiterName: role.recruiterName || 'N/A'
        };
      });


      setSubmissions(mappedSubmissions);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setIsLoading(false);
    }
  };

  const handleStepClick = async (roleId, step) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BASE_URL}/api/recruitment/applications/role/${roleId}/progress`, 
        { progress: step },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setSubmissions(prev => prev.map(sub => 
        sub.id === roleId ? { ...sub, progress: step } : sub
      ));
    } catch (error) {
      console.error('Error updating step:', error);
    }
  };

  const [formData, setFormData] = useState({
    clientName: '',
    jobTitle: '',
    employmentType: 'Contract',
    location: '',
    candBudget: '',
    clientBudget: ''
  });

  // Handle simple sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Extract unique clients for client dropdown respecting active siteFilter (Onsite / Offshore) and date range
  const clientsList = useMemo(() => {
    const clientSet = new Set();
    const startStr = appliedDateFilters.startDate || dateFilters.startDate;
    const endStr = appliedDateFilters.endDate || dateFilters.endDate;

    const isDateMatch = (itemDate) => {
      if (!startStr || !endStr || !itemDate) return true;
      const start = new Date(`${startStr}T00:00:00.000Z`);
      const end = new Date(`${endStr}T23:59:59.999Z`);
      const d = new Date(itemDate);
      return d >= start && d <= end;
    };

    submissions.forEach(s => {
      if (s.clientName && s.clientName !== 'N/A' && isRoleInDateRange(s, startStr, endStr)) {
        const isOff = isItemOffshore(s.location, s.clientName);
        if (!siteFilter || (siteFilter === 'Onsite' && !isOff) || (siteFilter === 'Offshore' && isOff)) {
          clientSet.add(s.clientName.trim());
        }
      }
    });
    candidatesData.forEach(c => {
      if (c.clientName && c.clientName !== 'N/A' && isDateMatch(c.createdAt || c.appliedAt)) {
        const isOff = isItemOffshore(c.jobLocation, c.clientName);
        if (!siteFilter || (siteFilter === 'Onsite' && !isOff) || (siteFilter === 'Offshore' && isOff)) {
          clientSet.add(c.clientName.trim());
        }
      }
    });
    return Array.from(clientSet).sort((a, b) => a.localeCompare(b));
  }, [submissions, candidatesData, siteFilter, appliedDateFilters, dateFilters]);

  // If active selectedClient is not part of the filtered clientsList for the selected site, clear it
  useEffect(() => {
    if (selectedClient && clientsList.length > 0 && !clientsList.includes(selectedClient)) {
      setSelectedClient('');
    }
  }, [siteFilter, clientsList, selectedClient]);

  // Base Filtered Jobs (Respects Search, Client, Date Filters, Advanced Rules, and Site Filter)
  const baseFilteredJobs = useMemo(() => {
    let result = submissions;

    if (searchQuery.trim()) {
      const queryParts = searchQuery.toLowerCase().split(',').map(p => p.trim()).filter(Boolean);
      result = result.filter(s => (
        queryParts.every(part => (
          (s.clientName && s.clientName.toLowerCase().includes(part)) ||
          (s.jobTitle && s.jobTitle.toLowerCase().includes(part)) ||
          (s.jobId && String(s.jobId).toLowerCase().includes(part)) ||
          (s.jobCode && String(s.jobCode).toLowerCase().includes(part)) ||
          (s.externalJobRef && String(s.externalJobRef).toLowerCase().includes(part)) ||
          (s.employmentType && s.employmentType.toLowerCase().includes(part)) ||
          (s.experience && String(s.experience).toLowerCase().includes(part)) ||
          (s.location && s.location.toLowerCase().includes(part)) ||
          (s.recruiterName && s.recruiterName.toLowerCase().includes(part))
        ))
      ));
    }

    if (selectedClient.trim()) {
      result = result.filter(s => (s.clientName || '').toLowerCase().includes(selectedClient.toLowerCase().trim()));
    }

    // Date Filtering (Inclusive: matches if role created in range OR if submissions made in range)
    if (appliedDateFilters.startDate && appliedDateFilters.endDate) {
      result = result.filter(s => isRoleInDateRange(s, appliedDateFilters.startDate, appliedDateFilters.endDate));
    }

    // Apply Advanced Filters if they exist and are valid
    const activeRules = filterRules.filter(r => r.field && r.condition && r.value);
    if (activeRules.length > 0) {
      result = result.filter(item => {
        let match = true;
        activeRules.forEach((rule, index) => {
          const itemValue = String(item[rule.field] || '').toLowerCase();
          const targetValue = rule.value.toLowerCase();
          let currentMatch = false;

          switch (rule.condition) {
            case 'equals': currentMatch = itemValue === targetValue; break;
            case 'contains': currentMatch = itemValue.includes(targetValue); break;
            case 'starts': currentMatch = itemValue.startsWith(targetValue); break;
            default: currentMatch = true;
          }

          if (index === 0) {
            match = currentMatch;
          } else {
            if (rule.operator === 'AND') {
              match = match && currentMatch;
            } else {
              match = match || currentMatch;
            }
          }
        });
        return match;
      });
    }

    if (siteFilter === 'Onsite') {
      result = result.filter(s => !isItemOffshore(s.location, s.clientName));
    } else if (siteFilter === 'Offshore') {
      result = result.filter(s => isItemOffshore(s.location, s.clientName));
    }

    return result;
  }, [submissions, searchQuery, selectedClient, appliedDateFilters, filterRules, siteFilter]);

  // Summary counts for roles filter (dynamically responds to active date, client and site filters)
  const totalRolesCount = useMemo(() => baseFilteredJobs.length, [baseFilteredJobs]);
  const withSubmissionsCount = useMemo(() => baseFilteredJobs.filter(s => (s.applicationCount || 0) > 0 || (s.progress || 0) > 0).length, [baseFilteredJobs]);
  const withoutSubmissionsCount = useMemo(() => baseFilteredJobs.filter(s => (s.applicationCount || 0) === 0 && (s.progress || 0) === 0).length, [baseFilteredJobs]);

  // Helper to identify onboarding candidate
  const isOnboardingCandidate = useCallback((row) => {
    if (row.onboardingInstanceId || row.onboardingStatusCode) return true;
    const stageCode = (row.currentStageCode || '').toUpperCase();
    const stepName = (hiringSteps[row.progress]?.stepName || '').toUpperCase();
    const statusText = (row.status || '').toUpperCase();
    if (stageCode === 'ONBOARDING_INITIATED' || stageCode === 'ONBOARDING_COMPLETED') return true;
    if (stepName.includes('ONBOARDING')) return true;
    if (statusText === 'ONBOARDING_INITIATED' || statusText === 'ONBOARDING_COMPLETED') return true;
    return false;
  }, [hiringSteps]);

  // Helper to format onboarding status badge
  const getOnboardingDisplay = (row) => {
    if (row.onboardingStatusCode === 'COMPLETED' || row.currentStageCode === 'ONBOARDING_COMPLETED') {
      return { label: 'Onboarding Completed', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' };
    }
    if (row.onboardingStatusCode === 'IN_PROGRESS' || row.currentStageCode === 'ONBOARDING_INITIATED' || (hiringSteps[row.progress]?.stepName || '').toUpperCase().includes('ONBOARDING')) {
      return { label: 'Onboarding Initiated', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' };
    }
    return null;
  };

  // Onboarding submissions count
  const onboardingCount = useMemo(() => {
    return candidatesData.filter(isOnboardingCandidate).length;
  }, [candidatesData, isOnboardingCandidate]);

  // Process data (Filter + Sort)
  const filteredSubmissions = useMemo(() => {
    if (searchType === 'candidates' || searchType === 'onboarding') {
      let result = candidatesData;

      if (searchType === 'onboarding') {
        result = result.filter(isOnboardingCandidate);
      }

      if (searchQuery.trim()) {
        const queryParts = searchQuery.toLowerCase().split(',').map(p => p.trim()).filter(Boolean);
        result = result.filter(s => (
          queryParts.every(part => (
            (s.clientName && s.clientName.toLowerCase().includes(part)) ||
            (s.jobTitle && s.jobTitle.toLowerCase().includes(part)) ||
            (s.jobCode && String(s.jobCode).toLowerCase().includes(part)) ||
            (s.candidateName && s.candidateName.toLowerCase().includes(part)) ||
            (s.email && s.email.toLowerCase().includes(part)) ||
            (s.phone && s.phone.toLowerCase().includes(part)) ||
            (s.submittedBy && s.submittedBy.toLowerCase().includes(part)) ||
            (s.jobLocation && s.jobLocation.toLowerCase().includes(part))
          ))
        ));
      }

      if (selectedClient.trim()) {
        result = result.filter(s => (s.clientName || '').toLowerCase().includes(selectedClient.toLowerCase().trim()));
      }

      if (appliedDateFilters.startDate && appliedDateFilters.endDate) {
        const start = new Date(`${appliedDateFilters.startDate}T00:00:00.000Z`);
        const end = new Date(`${appliedDateFilters.endDate}T23:59:59.999Z`);
        result = result.filter(s => {
          if (!s.createdAt) return true;
          const sDate = new Date(s.createdAt);
          return sDate >= start && sDate <= end;
        });
      }

      if (siteFilter === 'Onsite') {
        result = result.filter(s => !isItemOffshore(s.jobLocation, s.clientName));
      } else if (siteFilter === 'Offshore') {
        result = result.filter(s => isItemOffshore(s.jobLocation, s.clientName));
      }

      if (sortConfig.key) {
        result.sort((a, b) => {
          if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
          if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return result;
    }

    // searchType === 'jobs'
    let result = [...baseFilteredJobs];

    if (submissionStatusFilter === 'without_submissions') {
      result = result.filter(s => (s.applicationCount || 0) === 0 && (s.progress || 0) === 0);
    } else {
      result = result.filter(s => (s.applicationCount || 0) > 0 || (s.progress || 0) > 0);
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [searchType, candidatesData, baseFilteredJobs, searchQuery, selectedClient, appliedDateFilters, siteFilter, sortConfig, submissionStatusFilter, isOnboardingCandidate]);

  // Pagination Logic
  const paginatedSubmissions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredSubmissions.slice(startIndex, startIndex + pageSize);
  }, [filteredSubmissions, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredSubmissions.length / pageSize);

  // Reset to first page when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterRules, appliedDateFilters, siteFilter, submissionStatusFilter]);




  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCreateSubmission = (e) => {
    e.preventDefault();
    const newEntry = {
      ...formData,
      id: submissions.length + 1,
      clientId: `${Math.floor(Math.random() * 10000000)}`,
      progress: 1,
      createdAt: new Date().toISOString(),
      recruiters: ['ME'],
      experience: '0-2 years',
      submitted: formData.candBudget
    };
    setSubmissions([newEntry, ...submissions]);
    setIsModalOpen(false);
    setFormData({ clientName: '', jobTitle: '', employmentType: 'Contract', location: '', candBudget: '', clientBudget: '' });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60); // minutes
    if (diff < 60) return `${diff}m ago`;
    const hours = Math.floor(diff / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  const handleQuickSelectChange = (e) => {
    const val = e.target.value;
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    if (val === 'Today') {
      start = new Date(today);
      end = new Date(today);
    } else if (val === 'Yesterday') {
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
      end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    } else if (val === 'This Week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1); 
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today);
    } else if (val === 'Last Week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1) - 7;
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today.getFullYear(), today.getMonth(), diff + 6);
    } else if (val === 'This Month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    } else if (val === 'Last Month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (val === 'This Quarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today);
    } else if (val === 'Last Quarter') {
      const quarter = Math.floor(today.getMonth() / 3) - 1;
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
    }

    const sDate = toYMD(start);
    const eDate = toYMD(end);

    setDateFilters({
      quickSelect: val,
      startDate: sDate,
      endDate: eDate
    });

    // Automatically apply when selecting from Quick Select
    setAppliedDateFilters({
      startDate: sDate,
      endDate: eDate
    });
  };

  return (
    <div className="submissions-container">
      {/* Top Main Row: Title, Search, Controls, Action */}
      <div className="submissions-main-header">
        <div className="header-left-section">
          <h1 className="submissions-main-title">Submissions</h1>
          <div className="submissions-breadcrumbs">
            <span>Home</span>
            <span>&gt;</span>
            <span 
              onClick={() => setSearchType('jobs')}
              style={{ 
                cursor: 'pointer', 
                color: searchType === 'jobs' ? '#3b5bfd' : '#64748b', 
                fontWeight: searchType === 'jobs' ? '600' : 'normal',
                textDecoration: searchType === 'jobs' ? 'underline' : 'none'
              }}
            >
              Jobs
            </span>
            <span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span>
            <span 
              onClick={() => setSearchType('candidates')}
              style={{ 
                cursor: 'pointer', 
                color: searchType === 'candidates' ? '#3b5bfd' : '#64748b', 
                fontWeight: searchType === 'candidates' ? '600' : 'normal',
                textDecoration: searchType === 'candidates' ? 'underline' : 'none'
              }}
            >
              Candidates
            </span>
          </div>
        </div>

        <div className="header-middle-section">
          <div className="submissions-search-wrapper">
            <Search className="submissions-search-icon" size={16} />
            <input 
              type="text" 
              className="submissions-search-input" 
              placeholder="Filter by Client or Position..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="header-right-section" style={{ display: 'flex', alignItems: 'center' }}>
          <div className="site-filter-controls" style={{ display: 'flex', gap: '8px', marginRight: '16px' }}>
             {/* <button 
                style={{
                  padding: '6px 16px',
                  borderRadius: '20px',
                  border: searchType === 'onboarding' ? '1px solid #2563eb' : '1px solid #e2e8f0',
                  background: searchType === 'onboarding' ? '#eff6ff' : '#fff',
                  color: searchType === 'onboarding' ? '#1d4ed8' : '#64748b',
                  fontSize: '13px',
                  fontWeight: searchType === 'onboarding' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onClick={() => setSearchType(searchType === 'onboarding' ? 'jobs' : 'onboarding')}
                title="Filter Candidates in Onboarding Status"
             >
               Onboarding ({onboardingCount})
             </button> */}
             <button 
                style={{
                  padding: '6px 16px',
                  borderRadius: '20px',
                  border: siteFilter === 'Onsite' ? '1px solid #3b5bfd' : '1px solid #e2e8f0',
                  background: siteFilter === 'Onsite' ? '#3b5bfd' : '#fff',
                  color: siteFilter === 'Onsite' ? '#fff' : '#64748b',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setSiteFilter(siteFilter === 'Onsite' ? '' : 'Onsite')}
             >
               Onsite
             </button>
             <button 
                style={{
                  padding: '6px 16px',
                  borderRadius: '20px',
                  border: siteFilter === 'Offshore' ? '1px solid #3b5bfd' : '1px solid #e2e8f0',
                  background: siteFilter === 'Offshore' ? '#3b5bfd' : '#fff',
                  color: siteFilter === 'Offshore' ? '#fff' : '#64748b',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setSiteFilter(siteFilter === 'Offshore' ? '' : 'Offshore')}
             >
               Offshore
             </button>
          </div>
          <div className="submissions-controls">
            <button 
              className={`submissions-icon-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid size={18} />
            </button>
            <button 
              className={`submissions-icon-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Date Filters Section */}
      <div style={{
        background: '#fff',
        borderRadius: '8px',
        padding: '12px 16px',
        margin: '0 0 16px 0',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'flex-end',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Date filters:</span>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Quick Select</label>
              <select 
                value={dateFilters.quickSelect}
                onChange={handleQuickSelectChange}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  fontSize: '14px',
                  minWidth: '140px',
                  color: '#334155'
                }}
              >
                <option value="Today">Today</option>
                <option value="Yesterday">Yesterday</option>
                <option value="This Week">This Week</option>
                <option value="Last Week">Last Week</option>
                <option value="This Month">This Month</option>
                <option value="Last Month">Last Month</option>
                <option value="This Quarter">This Quarter</option>
                <option value="Last Quarter">Last Quarter</option>
                <option value="Custom">Custom</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Start date</label>
              <input 
                type="date"
                value={dateFilters.startDate}
                onChange={(e) => setDateFilters({ ...dateFilters, startDate: e.target.value, quickSelect: 'Custom' })}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  fontSize: '14px',
                  color: '#334155'
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>End date</label>
              <input 
                type="date"
                value={dateFilters.endDate}
                onChange={(e) => setDateFilters({ ...dateFilters, endDate: e.target.value, quickSelect: 'Custom' })}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  fontSize: '14px',
                  color: '#334155'
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>Client</label>
              <select
                value={selectedClient}
                onChange={(e) => {
                  setSelectedClient(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  outline: 'none',
                  fontSize: '14px',
                  minWidth: '150px',
                  color: '#334155',
                  height: '38px',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Clients</option>
                {clientsList.map((cName, idx) => (
                  <option key={idx} value={cName}>{cName}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <button 
          style={{
            padding: '8px 24px',
            borderRadius: '6px',
            border: '1px solid #3b5bfd',
            background: '#eff6ff',
            color: '#3b5bfd',
            fontWeight: '500',
            fontSize: '14px',
            cursor: 'pointer',
            height: '38px'
          }}
          onClick={() => {
            setAppliedDateFilters({
              startDate: dateFilters.startDate,
              endDate: dateFilters.endDate
            });
          }}
        >
          Apply
        </button>
        
        {(dateFilters.quickSelect !== 'Today' || 
          dateFilters.startDate !== today || 
          dateFilters.endDate !== today || 
          siteFilter !== 'Onsite' || 
          searchQuery !== '' ||
          selectedClient !== ''
        ) && (
          <button
            style={{
              padding: '8px 24px',
              borderRadius: '6px',
              border: '1px solid #e74c3c',
              background: '#fff5f5',
              color: '#e74c3c',
              fontWeight: '500',
              fontSize: '14px',
              cursor: 'pointer',
              height: '38px'
            }}
            onClick={() => {
              handleClearDateFilters();
              const todayVal = toYMD(new Date());
              setAppliedDateFilters({
                startDate: todayVal,
                endDate: todayVal
              });
              setSiteFilter('Onsite');
              setSelectedClient('');
              sessionStorage.removeItem('submission_selectedClient');
              setSearchQuery('');
              setPageSize(25);
              setCurrentPage(1);
              sessionStorage.removeItem('submission_siteFilter');
              sessionStorage.removeItem('submission_pageSize');
              sessionStorage.removeItem('submission_currentPage');
              sessionStorage.removeItem('submission_searchQuery');
            }}
          >
            ✕ Clear All Filters
          </button>
        )}
      </div>

      {/* Role Submission Status Filter (When in Jobs searchType) */}
      {searchType === 'jobs' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          margin: '0 0 16px 0',
          padding: '10px 16px',
          background: '#fff',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>
            Filter Roles:
          </span>
          <div style={{ display: 'flex', gap: '6px', background: '#f8fafc', padding: '3px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <button
              type="button"
              onClick={() => setSubmissionStatusFilter('with_submissions')}
              style={{
                padding: '5px 12px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: submissionStatusFilter === 'with_submissions' ? 700 : 500,
                background: submissionStatusFilter === 'with_submissions' ? '#3b5bfd' : 'transparent',
                color: submissionStatusFilter === 'with_submissions' ? '#fff' : '#64748b',
                transition: 'all 0.15s ease'
              }}
            >
              With Submissions ({withSubmissionsCount})
            </button>
            <button
              type="button"
              onClick={() => setSubmissionStatusFilter('without_submissions')}
              style={{
                padding: '5px 12px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: submissionStatusFilter === 'without_submissions' ? 700 : 500,
                background: submissionStatusFilter === 'without_submissions' ? '#ef4444' : 'transparent',
                color: submissionStatusFilter === 'without_submissions' ? '#fff' : '#64748b',
                transition: 'all 0.15s ease'
              }}
            >
              ⚠️ Without Submissions ({withoutSubmissionsCount})
            </button>
          </div>
        </div>
      )}

      {/* Conditional Rendering based on viewMode */}
      {viewMode === 'table' || searchType === 'candidates' ? (
        <div className="submissions-table-wrapper">
          <table className="submissions-table">
            <thead>
              {searchType === 'candidates' || searchType === 'onboarding' ? (
                <tr>
                  <th onClick={() => handleSort('candidateName')} className="sortable-th">
                    Candidate Name {sortConfig.key === 'candidateName' && <ArrowUpDown size={12} />}
                  </th>
                  <th onClick={() => handleSort('jobTitle')} className="sortable-th">
                    Job Title {sortConfig.key === 'jobTitle' && <ArrowUpDown size={12} />}
                  </th>
                  <th onClick={() => handleSort('clientName')} className="sortable-th">
                    Client {sortConfig.key === 'clientName' && <ArrowUpDown size={12} />}
                  </th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Exp</th>
                  <th>Location</th>
                  <th>{searchType === 'onboarding' ? 'Onboarding Status' : 'Progress'}</th>
                  <th>Submitted By</th>
                  <th>Created</th>
                </tr>
              ) : (
                <tr>
                  <th onClick={() => handleSort('clientName')} className="sortable-th">
                    Client {sortConfig.key === 'clientName' && <ArrowUpDown size={12} />}
                  </th>
                  <th>Job ID</th>
                  <th>External Job Ref</th>
                  <th onClick={() => handleSort('jobTitle')} className="sortable-th">
                    Job Title {sortConfig.key === 'jobTitle' && <ArrowUpDown size={12} />}
                  </th>
                  <th>Type</th>
                  <th>Exp</th>
                  <th>Location</th>
                  <th>Technical Progress</th>
                  <th>Created</th>
                </tr>
              )}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={searchType === 'candidates' ? "10" : "9"}>
                    <div className="submissions-loader-container">
                      <div className="submissions-loader"></div>
                      <div className="submissions-loader-text">Loading submissions...</div>
                    </div>
                  </td>
                </tr>
              ) : paginatedSubmissions.length > 0 ? (
                paginatedSubmissions.map((row) => (
                  searchType === 'candidates' || searchType === 'onboarding' ? (
                    <tr 
                      key={row.id} 
                      onClick={() => navigate(`/submissions/${row.jobId}`, { state: { jobInfo: row } })}
                      style={{ cursor: 'pointer' }}
                      className="clickable-row"
                    >
                      <td className="client-name" style={{ fontWeight: '600' }}>{row.candidateName}</td>
                      <td>
                        <span style={{ fontSize: '13px', fontWeight: '500' }}>{row.jobTitle}</span>
                      </td>
                      <td className="client-name">{row.clientName}</td>
                      <td style={{ fontSize: '12px' }}>{row.email}</td>
                      <td style={{ fontSize: '11px', color: '#64748b' }}>{row.phone}</td>
                      <td>{row.experience}</td>
                      <td className="na-text" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.location}>
                        {row.location}
                      </td>
                      <td title={hiringSteps[row.progress]?.stepName || `Step ${row.progress + 1}`}>
                        {(() => {
                          const badge = getOnboardingDisplay(row);
                          if (badge) {
                            return (
                              <span
                                className="badge progress-badge"
                                style={{
                                  backgroundColor: badge.bg,
                                  color: badge.color,
                                  border: `1px solid ${badge.border}`,
                                  fontWeight: 600,
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {badge.label}
                              </span>
                            );
                          }
                          return (
                            <span style={{ fontSize: '12px', fontWeight: '500', color: '#3b5bfd' }}>
                              {hiringSteps[row.progress]?.stepName || `Step ${row.progress + 1}`}
                            </span>
                          );
                        })()}
                      </td>
                      <td>{row.submittedBy}</td>
                      <td>{formatTime(row.createdAt)}</td>
                    </tr>
                  ) : (
                    <tr 
                      key={row.id} 
                      onClick={() => navigate(`/submissions/${row.id}`, { state: { jobInfo: row } })}
                      style={{ cursor: 'pointer' }}
                      className="clickable-row"
                    >
                      <td className="client-name">{row.clientName}</td>
                      <td style={{ fontSize: '11px', color: '#64748b' }}>{row.jobId}</td>
                      <td style={{ fontSize: '13px', fontWeight: '500', color: '#3b5bfd' }}>{row.externalJobRef}</td>
                      <td>
                        <Link to={`/submissions/${row.id}`} state={{ jobInfo: row }} className="job-title-link">{row.jobTitle}</Link>
                        {row.applicationCount === 0 && (
                          <span style={{ marginLeft: '8px', fontSize: '10px', background: '#fef2f2', color: '#dc2626', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                            0 Subs
                          </span>
                        )}
                      </td>
                      <td>{row.employmentType}</td>
                      <td>{row.experience}</td>
                      <td className="na-text" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.location}>
                        {row.location}
                      </td>
                      <td>
                        <ProgressChevron 
                          activeStep={row.progress} 
                          counts={row.stepCounts ? row.stepCounts.split(',').map(Number) : []}
                          rejectedStepCounts={row.rejectedStepCounts ? row.rejectedStepCounts.split(',').map(Number) : []}
                          withdrawnStepCounts={row.withdrawnStepCounts ? row.withdrawnStepCounts.split(',').map(Number) : []}
                          hiredStepCounts={row.hiredStepCounts ? row.hiredStepCounts.split(',').map(Number) : []}
                          onStepClick={(step) => handleStepClick(row.id, step)}
                          hiringSteps={hiringSteps}
                        />
                      </td>
                      <td title={`Job Created: ${new Date(row.jobCreatedAt || row.createdAt).toLocaleDateString()}${row.lastSubmissionAt ? ` | Latest Submission: ${new Date(row.lastSubmissionAt).toLocaleDateString()}` : ''}`}>
                        {formatTime(row.jobCreatedAt || row.createdAt)}
                      </td>
                    </tr>
                  )
                ))
              ) : (
                <tr>
                  <td colSpan={searchType === 'candidates' || searchType === 'onboarding' ? "10" : "9"} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                    {searchType === 'onboarding' 
                      ? 'No candidates currently in Onboarding status.'
                      : (submissionStatusFilter === 'without_submissions' 
                          ? 'No roles without submissions match your search.' 
                          : 'No submissions match your search.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="submissions-grid-wrapper">
          {isLoading ? (
            <div className="submissions-loader-container">
              <div className="submissions-loader"></div>
              <div className="submissions-loader-text">Loading submissions...</div>
            </div>
          ) : paginatedSubmissions.length > 0 ? (
            paginatedSubmissions.map((row) => (
              <div key={row.id} className="submission-card">
                <div className="card-top-status">Active</div>
                
                <div className="card-main-content">
                  {/* Left Section: Info */}
                  <div className="card-info-section">
                    <div className="building-icon-box">
                      <Briefcase size={24} className="building-icon" />
                    </div>
                    <div className="info-details">
                      <div className="client-id-row">{row.clientName} | {row.jobId} | External Ref: <span style={{ color: '#3b5bfd', fontWeight: '500' }}>{row.externalJobRef}</span></div>
                      <h3 className="job-title-card">
                        <Link to={`/submissions/${row.id}`} state={{ jobInfo: row }} style={{ textDecoration: 'none', color: 'inherit' }}>
                          {row.jobTitle}
                        </Link>
                        <span className="type-badge">{row.employmentType}</span>
                        {row.applicationCount === 0 && (
                          <span style={{ fontSize: '10px', background: '#fef2f2', color: '#dc2626', padding: '2px 6px', borderRadius: '4px', fontWeight: 600, marginLeft: '6px' }}>
                            0 Subs
                          </span>
                        )}
                      </h3>
                      <div className="meta-row">
                        <Clock size={12} /> <span>{row.experience}</span>
                      </div>
                      <div className="meta-row">
                        <User size={12} /> <span>{row.location}</span>
                      </div>
                    </div>
                  </div>

                  {/* Middle Sections: Budgets */}
                  <div className="card-budget-section">
                    <div className="budget-item">
                      <div className="budget-icon-group">
                        <Download size={14} /> 
                        <span className="budget-label">Rate</span>
                      </div>
                      <div className="budget-value">{row.rate}</div>
                    </div>
                  </div>

                  {/* Progress Section */}
                  <div className="card-progress-section">
                    <div className="pipeline-label">Hiring Pipeline</div>
                    <ProgressChevron 
                      activeStep={row.progress} 
                      counts={row.stepCounts ? row.stepCounts.split(',').map(Number) : []}
                      rejectedStepCounts={row.rejectedStepCounts ? row.rejectedStepCounts.split(',').map(Number) : []}
                      withdrawnStepCounts={row.withdrawnStepCounts ? row.withdrawnStepCounts.split(',').map(Number) : []}
                      hiredStepCounts={row.hiredStepCounts ? row.hiredStepCounts.split(',').map(Number) : []}
                      onStepClick={(step) => handleStepClick(row.id, step)}
                      hiringSteps={hiringSteps}
                    />

                  </div>

                  {/* Right Section: Time & Actions */}
                  <div className="card-actions-section">
                    <div className="time-display" title={`Job Created: ${new Date(row.jobCreatedAt || row.createdAt).toLocaleDateString()}${row.lastSubmissionAt ? ` | Latest Submission: ${new Date(row.lastSubmissionAt).toLocaleDateString()}` : ''}`}>
                      <Clock size={14} /> {formatTime(row.jobCreatedAt || row.createdAt)}
                    </div>
                    <div className="action-icons">

                      <div className="icon-utility"><Grid size={16} /></div>
                      <div className="icon-utility"><MoreVertical size={16} /></div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-data-grid">No submissions match your search.</div>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      <div className="pagination-footer">
        <div className="pagination-info">
          <div className="pagination-size-selector">
            <span>Show</span>
            <select 
              value={pageSize} 
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[10, 15, 20, 25, 30, 35, 40, 45, 50].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span>entries</span>
          </div>
          <span className="showing-text">
            Showing {filteredSubmissions.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, filteredSubmissions.length)} of {filteredSubmissions.length} entries
          </span>
        </div>
        <div className="pagination-controls">
          <button 
            className={`p-btn ${currentPage === 1 ? 'disabled' : ''}`}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={16} /> Previous
          </button>
          
          <div className="p-numbers">
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
               let pageNum;
               if (totalPages <= 5) pageNum = i + 1;
               else if (currentPage <= 3) pageNum = i + 1;
               else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
               else pageNum = currentPage - 2 + i;

               return (
                <button 
                  key={pageNum} 
                  className={`p-num ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
               );
            })}
          </div>

          <button 
            className={`p-btn ${currentPage === totalPages ? 'disabled' : ''}`}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>


      {/* Advanced Filter Modal */}
      {isFilterModalOpen && (
        <div className="sub-modal-overlay">
          <div className="adv-filter-modal">
            <div className="adv-filter-header">
              <div className="header-left">
                <h2>Filter</h2>
              </div>
              <div className="header-right">
                <button className="clear-all-btn" onClick={() => setFilterRules([{ id: Date.now(), field: '', condition: '', value: '', operator: 'AND' }])}>
                  Clear All
                </button>
                <X className="close-filter-icon" onClick={() => setIsFilterModalOpen(false)} />
              </div>
            </div>

            <div className="filter-rules-container">
              {filterRules.map((rule, index) => (
                <div key={rule.id} className="filter-rule-box">
                  {index > 0 && (
                    <div className="operator-select-wrapper">
                      <select 
                        className="operator-select"
                        value={rule.operator}
                        onChange={(e) => {
                          const newRules = [...filterRules];
                          newRules[index].operator = e.target.value;
                          setFilterRules(newRules);
                        }}
                      >
                        <option value="AND">AND</option>
                        <option value="OR">OR</option>
                      </select>
                    </div>
                  )}
                  
                  <div className="rule-row">
                    <div className="rule-field-group">
                      <label>FIELD*</label>
                      <select 
                        value={rule.field}
                        onChange={(e) => {
                          const newRules = [...filterRules];
                          newRules[index].field = e.target.value;
                          setFilterRules(newRules);
                        }}
                      >
                        <option value="">Field*</option>
                        <option value="clientName">Client</option>
                        <option value="jobTitle">Job Title</option>
                        <option value="employmentType">Type</option>
                        <option value="location">Location</option>
                      </select>
                    </div>

                    <div className="rule-field-group">
                      <label>CONDITION*</label>
                      <select 
                        value={rule.condition}
                        onChange={(e) => {
                          const newRules = [...filterRules];
                          newRules[index].condition = e.target.value;
                          setFilterRules(newRules);
                        }}
                      >
                        <option value="">Select</option>
                        <option value="equals">Equals</option>
                        <option value="contains">Contains</option>
                        <option value="starts">Starts With</option>
                      </select>
                    </div>

                    <div className="rule-field-group">
                      <label>VALUE</label>
                      <input 
                        placeholder="Value"
                        value={rule.value}
                        onChange={(e) => {
                          const newRules = [...filterRules];
                          newRules[index].value = e.target.value;
                          setFilterRules(newRules);
                        }}
                      />
                    </div>

                    <div className="rule-delete-action">
                      <button 
                        className="delete-rule-btn"
                        onClick={() => {
                          if (filterRules.length > 1) {
                            setFilterRules(filterRules.filter(r => r.id !== rule.id));
                          }
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button 
                className="add-filter-line-btn"
                onClick={() => setFilterRules([...filterRules, { id: Date.now(), field: '', condition: '', value: '', operator: 'AND' }])}
              >
                <Plus size={16} /> ADD FILTER
              </button>
            </div>

            <div className="adv-filter-footer">
              <button className="btn-secondary" onClick={() => setIsFilterModalOpen(false)}>Cancel</button>
              <button className="btn-primary apply-btn" onClick={() => setIsFilterModalOpen(false)}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Submission Modal */}
      {isModalOpen && (
        <div className="sub-modal-overlay">
          <div className="sub-modal-content">
            <div className="sub-modal-header">
              <h3>Create New Submission</h3>
              <button className="close-modal" onClick={() => setIsModalOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateSubmission}>
              <div className="sub-modal-grid">
                <div className="input-group">
                  <label>Client Name</label>
                  <input name="clientName" required value={formData.clientName} onChange={handleInputChange} placeholder="e.g. Microsoft" />
                </div>
                <div className="input-group">
                  <label>Job Title</label>
                  <input name="jobTitle" required value={formData.jobTitle} onChange={handleInputChange} placeholder="e.g. Senior Dev" />
                </div>
                <div className="input-group">
                  <label>Employment Type</label>
                  <select name="employmentType" value={formData.employmentType} onChange={handleInputChange}>
                    <option>Contract</option>
                    <option>Full-time</option>
                    <option>Contract-to-Hire</option>
                  </select>
                </div>
                <div className="input-group">
                  <label>Location</label>
                  <input name="location" value={formData.location} onChange={handleInputChange} placeholder="e.g. Austin, TX" />
                </div>
                <div className="input-group">
                  <label>Candidate Budget</label>
                  <input name="candBudget" value={formData.candBudget} onChange={handleInputChange} placeholder="$ 60 / hr" />
                </div>
                <div className="input-group">
                  <label>Client Budget</label>
                  <input name="clientBudget" value={formData.clientBudget} onChange={handleInputChange} placeholder="$ 70 / hr" />
                </div>
              </div>
              <div className="sub-modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Create Submission</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionDashboard;
